import {Observable} from 'rxjs';
import {CpmCCustomerUserMembershipApiModel} from '../../models/c-common/cpmc-customer-user-membership.api-model';
import {
    CpmCCustomerUserMembershipCreateApiSchema,
    CpmCCustomerUserMembershipUpdateApiSchema
} from '../../schemas/c-common/cpmc-customer-user-membership.api-schemas';
import {CpmCApiBridge} from '../../../../../src/app/cpmc-api-bridge';
import {CpmCQueryPaginatorConfig, CpmCQueryPaginatorConfigUtils} from '../../cpmc-defines';
import {AllowedQueryParamTypes, QueryParams} from '../../../../../sdk/src/utils/query.utils';
import {CpmCPaginatedResultAPISchema} from '../../schemas/cpmc-paginated-result.api-schemas';

export type CpmCGetCustomerUserMembershipListParams = {
    [K in keyof CpmCCustomerUserMembershipApiModel]?: CpmCCustomerUserMembershipApiModel[K] extends AllowedQueryParamTypes
        ? CpmCCustomerUserMembershipApiModel[K]
        : never;
} & {} & QueryParams;

/**
 * Customer User Membership Api Service
 */
export class CpmCCustomerUserMembershipApiService {
    public static readonly dPaginatorConfig: CpmCQueryPaginatorConfig = {limit: -1, sort: 'user.email', order: 1};

    // ===============================================================
    // ===============================================================

    public static baseUrl(inCustomerUUID: string | undefined) {
        const base = CpmCApiBridge.getBackendBaseURL() + 'api/core/v1/';
        const customerUUID = CpmCApiBridge.getCurrentCustomerUUID();

        if (inCustomerUUID !== undefined || (inCustomerUUID === undefined && customerUUID)) {
            return base + `customer/${inCustomerUUID || customerUUID}/customer-user-membership`;
        } else {
            return base + `customer-user-membership`;
        }
    }

    // ===============================================================
    // ===============================================================

    public static endpointList(inCustomerUUID: string | undefined): string {
        return `${CpmCCustomerUserMembershipApiService.baseUrl(inCustomerUUID)}/list`;
    }
    public static endpointNew(inCustomerUUID: string | undefined): string {
        return `${CpmCCustomerUserMembershipApiService.baseUrl(inCustomerUUID)}`;
    }
    public static endpointGet(inCustomerUUID: string | undefined, customerUserMembershipUUID: string): string {
        return `${CpmCCustomerUserMembershipApiService.baseUrl(inCustomerUUID)}/${customerUserMembershipUUID}`;
    }
    public static endpointEdit(inCustomerUUID: string | undefined, customerUserMembershipUUID: string): string {
        return `${CpmCCustomerUserMembershipApiService.baseUrl(inCustomerUUID)}/${customerUserMembershipUUID}`;
    }
    public static endpointDelete(inCustomerUUID: string | undefined, customerUserMembershipUUID: string): string {
        return `${CpmCCustomerUserMembershipApiService.baseUrl(inCustomerUUID)}/${customerUserMembershipUUID}`;
    }

    // ===============================================================
    // ===============================================================

    public static getCustomerUserMembershipList(
        params: CpmCGetCustomerUserMembershipListParams = {},
        paginatorConfig: CpmCQueryPaginatorConfig = CpmCCustomerUserMembershipApiService.dPaginatorConfig
    ): Observable<CpmCPaginatedResultAPISchema<CpmCCustomerUserMembershipApiModel>> {
        return CpmCApiBridge.httpGet<CpmCPaginatedResultAPISchema<CpmCCustomerUserMembershipApiModel>>(
            CpmCCustomerUserMembershipApiService.endpointList(undefined),
            {
                ...(params as QueryParams),
                ...CpmCQueryPaginatorConfigUtils.toQueryParams(paginatorConfig)
            }
        );
    }
    public static getCustomerUserMembershipByUUID(
        customerUserMembershipUUID: string
    ): Observable<CpmCCustomerUserMembershipApiModel> {
        return CpmCApiBridge.httpGet<CpmCCustomerUserMembershipApiModel>(
            CpmCCustomerUserMembershipApiService.endpointGet(undefined, customerUserMembershipUUID)
        );
    }
    public static createCustomerUserMembership(
        payload: CpmCCustomerUserMembershipCreateApiSchema
    ): Observable<CpmCCustomerUserMembershipApiModel> {
        return CpmCApiBridge.httpPost<CpmCCustomerUserMembershipApiModel>(
            CpmCCustomerUserMembershipApiService.endpointNew(undefined),
            payload
        );
    }
    public static updateCustomerUserMembership(
        customerUserMembershipUUID: string,
        payload: CpmCCustomerUserMembershipUpdateApiSchema
    ): Observable<CpmCCustomerUserMembershipApiModel> {
        return CpmCApiBridge.httpPut<CpmCCustomerUserMembershipApiModel>(
            CpmCCustomerUserMembershipApiService.endpointEdit(undefined, customerUserMembershipUUID),
            payload
        );
    }
    public static deleteCustomerUserMembership(customerUserMembershipUUID: string): Observable<boolean> {
        return CpmCApiBridge.httpDelete<boolean>(
            CpmCCustomerUserMembershipApiService.endpointDelete(undefined, customerUserMembershipUUID)
        );
    }
}
